import { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useBlocker, useNavigation, useLocation } from 'react-router';
import { T } from '@transifex/react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '~/components/ui/dialog';

export type AutosaveStatus = {
  isAutosaving: boolean;
  lastAutosaveTime: Date | null;
  hasChanges: boolean;
};

type AutosaveContextType = {
  autosaveStatus: AutosaveStatus;
  setAutosaveStatus: (status: AutosaveStatus | ((prev: AutosaveStatus) => AutosaveStatus)) => void;
  setHasChanges: (hasChanges: boolean) => void;
  markAsSaved: () => void;
  resetAutosave: () => void;
};

const defaultAutosaveContext: AutosaveContextType = {
  autosaveStatus: {
    isAutosaving: false,
    lastAutosaveTime: null,
    hasChanges: false,
  },
  setAutosaveStatus: () => {},
  setHasChanges: () => {},
  markAsSaved: () => {},
  resetAutosave: () => {},
};

const AutosaveContext = createContext<AutosaveContextType>(defaultAutosaveContext);

export function AutosaveProvider({ children }: { children: ReactNode }) {
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>(
    defaultAutosaveContext.autosaveStatus
  );

  const navigation = useNavigation();
  const location = useLocation();

  const isSaving = navigation.state === 'submitting';

  // Helper function to update hasChanges through autosaveStatus
  const setHasChanges = (hasChanges: boolean) => {
    if (hasChanges === autosaveStatus.hasChanges) return;

    setAutosaveStatus(prev => ({
      ...prev,
      hasChanges,
    }));
  };

  const markAsSaved = () => {
    setAutosaveStatus(prev => ({
      ...prev,
      hasChanges: false,
      isAutosaving: false,
      lastAutosaveTime: new Date(),
    }));
  };

  const resetAutosave = () => {
    setAutosaveStatus(defaultAutosaveContext.autosaveStatus);
  };

  // Reset lastAutosaveTime when navigating to a different section
  useEffect(() => {
    setAutosaveStatus(prev => ({
      ...prev,
      lastAutosaveTime: null,
    }));
  }, [location.pathname]);

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      autosaveStatus.hasChanges && !isSaving && currentLocation.pathname !== nextLocation.pathname
  );

  // Handle browser refresh/close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (autosaveStatus.hasChanges && !isSaving) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [autosaveStatus.hasChanges, isSaving]);

  const contextValue = {
    autosaveStatus,
    setAutosaveStatus,
    setHasChanges,
    markAsSaved,
    resetAutosave,
  };

  return (
    <AutosaveContext.Provider value={contextValue}>
      <ConfirmNavigation
        open={blocker.state === 'blocked'}
        reset={blocker.reset}
        proceed={() => {
          blocker.proceed?.();
          setHasChanges(false);
        }}
      />
      {children}
    </AutosaveContext.Provider>
  );
}

export { AutosaveContext };

function ConfirmNavigation({
  open,
  reset,
  proceed,
}: {
  open: boolean;
  reset?: () => void;
  proceed?: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-[400px]">
        <DialogHeader>
          <DialogTitle>
            <T _str="Unsaved Changes" />
          </DialogTitle>
          <DialogDescription>
            <T _str="You have unsaved changes. Are you sure you want to leave this page?" />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={reset}>
            <T _str="Stay" />
          </Button>
          <Button type="button" variant="destructive" onClick={proceed}>
            <T _str="Leave" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
